import { controlBandChipFieldSx } from "../../theme/compact.js";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { useMemo, useState, type ReactElement } from "react";

/**
 * `<TypeaheadChipFilter>` — multi-select chip filter with **hidden
 * options until the user types**, filtered client-side.
 *
 * Spec: `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 5 /
 * TypeaheadChipFilter".
 *
 * Use when the option universe is moderate (~tens to a few hundred
 * entries) and is fetched once page-side. The full list is held in
 * memory; substring-filtered on every keystroke without a server
 * roundtrip. Use `<AsyncTypeaheadChipFilter>` instead when the
 * universe is large enough that a server-side trigram lookup is
 * cheaper than shipping every row.
 *
 * UX:
 * - Dropdown shows **no options** while the input is empty — the
 *   user types to narrow rather than scrolling a long list.
 *   Override with `eagerOptions` (see below) for small enum filters
 *   where typing to narrow doesn't earn its keep.
 * - Already-selected chips remain visible regardless of input.
 * - 280 px wide, MUI Autocomplete with `disableCloseOnSelect`.
 * - Floating label always shrunk so it doesn't overlap the field.
 */
export interface TypeaheadChipFilterProps<T> {
  label: string;
  value: readonly string[];
  onChange: (next: readonly string[]) => void;
  options: readonly T[];
  getOptionId: (option: T) => string;
  getOptionLabel: (option: T) => string;
  /** Per-page width override. Defaults to 280 px (matches `<MultiSelectChipFilter>`). */
  width?: number;
  /** Customise the empty-state hint. Defaults to "Type to search". */
  noOptionsText?: string;
  /**
   * SB6-CONS-03 — when `true`, the dropdown shows ALL `options`
   * immediately on open (no typeahead-filtering required to reveal the
   * list). Typed input still narrows the list. Use for small enum
   * filters (Contract states, Position states — ~10 entries) where the
   * "type to search" affordance is more friction than help.
   *
   * Default `false` preserves the canonical typeahead UX for the
   * moderate-cardinality cases (Projects, Sites, Customers,
   * Contractors, Assets).
   */
  eagerOptions?: boolean;
}

export function TypeaheadChipFilter<T>({
  label,
  value,
  onChange,
  options,
  getOptionId,
  getOptionLabel,
  width = 280,
  noOptionsText = "Type to search",
  eagerOptions = false,
}: TypeaheadChipFilterProps<T>): ReactElement {
  const [input, setInput] = useState("");

  const optionById = useMemo(() => {
    const map = new Map<string, T>();
    for (const o of options) map.set(getOptionId(o), o);
    return map;
  }, [options, getOptionId]);

  const selected = useMemo<T[]>(
    () => value.map((id) => optionById.get(id)).filter((o): o is T => !!o),
    [value, optionById],
  );

  // SB6-CONS-03 — when `eagerOptions` is true and the input is empty,
  // surface ALL options (not just the selected ones) so the dropdown
  // shows the full enum on open. The typed-input path is unchanged —
  // a substring still narrows the list. Default-false keeps the
  // canonical typeahead UX for everything else.
  const filteredHits = useMemo<T[]>(() => {
    const q = input.trim().toLowerCase();
    if (q.length === 0) return eagerOptions ? [...options] : [];
    return options.filter((o) => getOptionLabel(o).toLowerCase().includes(q));
  }, [input, options, getOptionLabel, eagerOptions]);

  const optionUnion = useMemo<T[]>(() => {
    const seen = new Set<string>();
    const out: T[] = [];
    for (const o of selected) {
      const id = getOptionId(o);
      if (!seen.has(id)) {
        seen.add(id);
        out.push(o);
      }
    }
    for (const o of filteredHits) {
      const id = getOptionId(o);
      if (!seen.has(id)) {
        seen.add(id);
        out.push(o);
      }
    }
    return out;
  }, [selected, filteredHits, getOptionId]);

  return (
    <Autocomplete<T, true>
      multiple
      disableCloseOnSelect
      size="small"
      filterOptions={(x) => x}
      options={optionUnion}
      value={selected}
      inputValue={input}
      onInputChange={(_e, next, reason) => {
        if (reason === "reset") return;
        setInput(next);
      }}
      isOptionEqualToValue={(a, b) => getOptionId(a) === getOptionId(b)}
      getOptionLabel={getOptionLabel}
      onChange={(_e, next) => {
        // Clear-on-pick per spec §5/Clear-on-close-or-pick — without
        // this, the typed substring survives across picks and the
        // user can't recover the full list.
        setInput("");
        onChange(next.map((o) => getOptionId(o)));
      }}
      onClose={() => {
        // Clear-on-close per spec §5/Clear-on-close-or-pick.
        setInput("");
      }}
      sx={{
        // The control band. MISSED in #437, which is what left the
        // Turbines toolbar mixing 40px typeaheads with 32px filters
        // (Christian, 2026-09-23).
        ...controlBandChipFieldSx,
        width,
        flexShrink: 0,
        // SB6-II review fix — cap each chip's width so it never fills the whole
        // field; this reserves room for the inner <input> on the SAME row, so a
        // single picked chip no longer pushes the (empty) input onto a phantom
        // second line. Chips still wrap to real rows as more are picked.
        "& .MuiAutocomplete-tag": { maxWidth: "calc(100% - 44px)" },
      }}
      renderTags={(picked, getTagProps) =>
        picked.map((o, index) => {
          const { key, ...rest } = getTagProps({ index });
          return (
            <Chip {...rest} key={key} size="small" label={getOptionLabel(o)} />
          );
        })
      }
      renderInput={(p) => {
        const { InputLabelProps, InputProps, inputProps, ...rest } = p;
        // SB6-CONS-01 — see MultiSelectChipFilter for the rationale.
        // `minWidth: 0` lets the inner <input> sit next to the chips
        // when they fit on one row, removing the empty-row-below-chips
        // visual on Contracts / Contract-Positions / Users / Audit-Log.
        const tfProps = {
          ...rest,
          label,
          slotProps: {
            input: InputProps,
            inputLabel: { ...InputLabelProps, shrink: true },
            htmlInput: {
              ...inputProps,
              "aria-label": label,
              style: { minWidth: "0px" },
            },
          },
        } as unknown as React.ComponentProps<typeof TextField>;
        return <TextField {...tfProps} />;
      }}
      noOptionsText={
        input.trim().length === 0
          ? eagerOptions
            ? "No options"
            : noOptionsText
          : "No matches"
      }
    />
  );
}
